import { InjectBaseModalProps } from "@/hooks/Modal/ModalStore";
import {
    deletePurchasingLabel,
    MoreFeatureExportPurchasingLabelParams,
    PurchasingLabelProductSkuUpdatedInfo,
    StatsModel,
} from "@/services/api/moreFeatures/purchasingLabel/purchasingLabelStatistics";
import { Alert, Button, Modal, message } from "antd";
import moment from "moment";
import React, { useState } from "react";
import PurchasingLabelStatisticsProductTable from "./PurchasingLabelStatisticsProductTable";
import { printPurchasingLabelExcel } from "./util";
import "./index.less";
import { useModalStore } from "@/hooks/Modal/hooks";
import PrintPurchasingLabelModal from "../PrintPurchasingLabelModal";
import { downloadInPage } from "@/utils/dom";
import { useHistory } from "react-router-dom";
import ExportPurchasingLabelExcelButton from "./ExportPurchasingLabelExcelButton";
import { cloneDeep } from "lodash";
import { ProductSkuUpdatedInfoType } from "@/services/api/moreFeatures/pickList";
import { confirm } from "@/utils";
import { collectEvent } from "@/services/dataTrack";
import DataTrackEvent from "@/services/dataTrack/enum/DataTrackEvent";

interface PurchasingLabelStatisticsProductModalProps extends InjectBaseModalProps {
    statsModels: StatsModel[];
    distributedId: number;
    generatePurchasingLabelExportTaskParam: Pick<
        MoreFeatureExportPurchasingLabelParams,
        "exportItems" | "paidStart" | "paidEnd"
    >;
    onPrintDone?: () => void;
}

const PurchasingLabelStatisticsProductModal: React.FC<PurchasingLabelStatisticsProductModalProps> = function (props) {
    const { open, onCancel, statsModels, generatePurchasingLabelExportTaskParam, distributedId, onPrintDone } = props;
    const [loading, updateLoading] = useState(false);
    const { openModal } = useModalStore();
    const history = useHistory();
    const [operatedStatsModels, updateOperatedStatsModels] = useState(statsModels);
    const [allowDeleted, setAllowDeleted] = useState(true);

    function handlePrintPurchasingLabel() {
        setAllowDeleted(false);
        openModal(PrintPurchasingLabelModal, { distributedId, onPrintDone });

        collectEvent(DataTrackEvent.USE_PURCHASING_LABEL_FEATURE, { feature: "打印标签" });
    }

    // 删除市场档口
    async function handleDeleteMarketWholesaler(statsModelIndex: number) {
        await confirm("确认删除该市场档口吗？", <span className="text-error">删除后不导出商品信息</span>);

        const newStatsModels = cloneDeep(operatedStatsModels);
        const statsModel = newStatsModels[statsModelIndex];
        if (!statsModel?.productModels?.length) return;

        const productSkuUpdatedInfos: PurchasingLabelProductSkuUpdatedInfo[] = [];

        statsModel.productModels.forEach((productModel) => {
            if (!productModel?.skus?.length) return;

            productModel.skus.forEach((skuModel) => {
                const productOriginalId = productModel.originalId;
                const skuOriginalId = skuModel.originalId;

                productSkuUpdatedInfos.push({
                    productOriginalId,
                    skuOriginalId,
                    updateType: ProductSkuUpdatedInfoType.DELETED,
                    skuExternalId: skuModel.externalId,
                    productExternalId: productModel.externalId,
                    skuProperties: skuModel.name,
                });
            });
        });

        // 删除市场档口行
        newStatsModels.splice(statsModelIndex, 1);
        updateOperatedStatsModels(newStatsModels);

        await deletePurchasingLabel(distributedId, productSkuUpdatedInfos);
    }

    // 删除 SKU
    async function handleDeleteSku(statsModelIndex: number, productIndex: number, skuIndex: number) {
        await confirm("确认删除该商品吗？", <span className="text-error">删除后不导出商品信息</span>);
        const newStatsModels = cloneDeep(operatedStatsModels);
        const statsModel = newStatsModels[statsModelIndex];
        if (!statsModel?.productModels?.length) return;

        const productModel = statsModel.productModels[productIndex];
        if (!productModel?.skus?.length) return;
        const skuModel = productModel.skus[skuIndex];
        if (!skuModel) return;

        const productSkuUpdatedInfos: PurchasingLabelProductSkuUpdatedInfo[] = [];
        const productOriginalId = productModel.originalId;
        const skuOriginalId = skuModel.originalId;
        productSkuUpdatedInfos.push({
            productOriginalId,
            skuOriginalId,
            updateType: ProductSkuUpdatedInfoType.DELETED,
            skuExternalId: skuModel.externalId,
            productExternalId: productModel.externalId,
            skuProperties: skuModel.name,
        });

        productModel.skus.splice(skuIndex, 1);

        /** 该商品对应的所有sku已被删除，也将product删除 */
        if (productModel.skus.length === 0) {
            statsModel.productModels.splice(productIndex, 1);

            // 如果该市场档口下的商品都被删除，则删除该市场档口
            if (statsModel.productModels.length === 0) {
                newStatsModels.splice(statsModelIndex, 1);
            }
        }

        /** 重新计算总数 */
        productModel.total = productModel.skus?.reduce((accumulation, current) => accumulation + current.quantity, 0);
        statsModel.total = statsModel.productModels?.reduce((accumulation, current) => accumulation + current.total, 0);

        updateOperatedStatsModels(newStatsModels);
        await deletePurchasingLabel(distributedId, productSkuUpdatedInfos);
    }

    function handlePrintExcel() {
        setAllowDeleted(false);

        const { exportItems, paidStart, paidEnd } = generatePurchasingLabelExportTaskParam;
        const text = { range: "", exportTime: "" };

        if (!paidStart && !paidEnd) {
            text.range = "全部";
        } else {
            text.range = `${paidStart} - ${paidEnd}`;
        }

        text.exportTime = moment().format("yyyy-MM-DD HH:mm:ss");

        printPurchasingLabelExcel({
            statsModels: operatedStatsModels,
            onProcess: updateLoading,
            exportItems,
            text,
        });

        collectEvent(DataTrackEvent.USE_PURCHASING_LABEL_FEATURE, { feature: "打印EXCEL" });
    }

    function handleClickSettingMarket() {
        history.push("/features/purchasingLabel?tab=marketAndWholesalerSetting");

        onCancel();
    }

    return (
        <Modal open={open} onCancel={onCancel} width={1200} footer={null}>
            <Alert
                type="info"
                className="mb10"
                title={
                    <p>
                        打印拿货标签前，请先前往「备货拿货-商品拿货档口设置」中设置商品的市场档口信息。
                        <Button type="link" className="ml5" onClick={handleClickSettingMarket}>
                            去设置市场档口信息
                        </Button>
                        <a
                            href="https://qw15p3s8hv.feishu.cn/wiki/I77Ywo4bXiek80kChEGcZkoAnNh?from=from_copylink"
                            target="_blank"
                            className="ml5"
                        >
                            查看教程
                        </a>
                    </p>
                }
            />

            <div className="mb10">
                {statsModels?.length ? (
                    <>
                        <Button type="primary" onClick={handlePrintPurchasingLabel} loading={loading}>
                            打印拿货标签
                        </Button>

                        <ExportPurchasingLabelExcelButton
                            className="ant-btn-success  ml10"
                            distributedId={distributedId}
                            onSuccess={(downloadUrl) => {
                                downloadInPage(downloadUrl);

                                message.success("导出成功");
                            }}
                            onError={() => {
                                Modal.error({ title: "导出失败", content: distributedId });
                            }}
                            onClick={() => {
                                setAllowDeleted(false);

                                collectEvent(DataTrackEvent.USE_PURCHASING_LABEL_FEATURE, { feature: "导出EXCEL" });
                            }}
                        />

                        <Button onClick={handlePrintExcel} className="ant-btn-warning  ml10">
                            打印Excel
                        </Button>
                    </>
                ) : null}
            </div>

            <PurchasingLabelStatisticsProductTable
                statsModels={operatedStatsModels}
                exportItems={generatePurchasingLabelExportTaskParam.exportItems}
                className="display"
                onDeleteMarketWholesaler={allowDeleted ? handleDeleteMarketWholesaler : null}
                onDeleteSku={allowDeleted ? handleDeleteSku : null}
            />
        </Modal>
    );
};

export default React.memo(PurchasingLabelStatisticsProductModal);
